/**
 * Global state for tracking draft and pending chat messages.
 *
 * This allows:
 * - Preserving partially authored messages across component unmount/remount
 * - Showing draft indicators in the chat listing
 * - Tracking file uploads through their lifecycle (uploading -> completed, removed on failure)
 *
 * State is keyed by chatSessionId for existing chats, or NEW_CHAT_DRAFT_KEY for new chats.
 */

import {useMemo} from 'react';

import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

import {CreateDisplayMessageChatEvent, getContextIdsFromActiveWorkspace, getUserChatContext} from './ChatDAO';
import {getCompletedUploads} from './ChatFileHelper';
import {ChatSelectedFile} from './ChatLibraryTypes';
import {
  doesAgentTypeSupportPublicData,
  doesAgentTypeSupportSources,
  getDefaultAgentType,
} from './sidepanel/ChatTypeUtil';

/** Sentinel key for tracking drafts in new chats that haven't been persisted yet. */
export const NEW_CHAT_DRAFT_KEY = '__NEW_CHAT__';

export enum ChatDraftStatus {
  /** User is composing a message but hasn't sent it yet. */
  DRAFT = 'DRAFT',
  /** Message has been submitted and is awaiting server response. */
  PENDING = 'PENDING',
}

export interface ChatDraftMessage {
  text: string;
  /** Files in the draft (uploading or completed) */
  selectedFiles: Array<ChatSelectedFile>;
}

const EMPTY_MESSAGE: ChatDraftMessage = {
  text: '',
  selectedFiles: [],
};

/**
 * Settings for a chat draft. When undefined on ChatDraftState, indicates the settings
 * are not yet known (e.g. for an existing chat that hasn't loaded yet).
 */
export interface ChatDraftSettings {
  agentType: AgentType;
  /**
   * TODO: This should be replaced by sources: `Array<{type: SOURCE_TYPE}>`.
   * For now, we use `getSources` to translate to the expected shape for the API.
   */
  publicDataEnabled: boolean;
}

export interface ChatDraftState {
  status: ChatDraftStatus;
  message: ChatDraftMessage;
  /** Undefined indicates settings are not yet known (e.g. for an existing chat, not yet loaded). */
  settings: ChatDraftSettings | undefined;
}

/**
 * Options for submitting a draft message.
 */
export interface CreateParams {
  /** Context IDs to include (file IDs, etc). Defaults to active workspace context. */
  contextIds?: Array<string>;
  /** Whether this is a retry of a failed message. */
  retry?: boolean;
}

// ============================================
// Conversion Helpers
// ============================================

/**
 * Derive sources from settings.
 *
 * TODO: ChatDraftState should replace publicDataEnabled with sources directly,
 * and the UI must be able to handle sources more generally.
 */
function getSources(settings: ChatDraftSettings): Array<{type: 'public_data'}> | undefined {
  const {agentType, publicDataEnabled} = settings;

  if (!doesAgentTypeSupportSources(agentType)) {
    return undefined;
  }

  const sources: Array<{type: 'public_data'}> = [];

  if (doesAgentTypeSupportPublicData(agentType) && publicDataEnabled) {
    sources.push({type: 'public_data'});
  }

  return sources;
}

/**
 * Convert a draft state to the format needed for creating a display message chat event.
 *
 * @throws Error if settings is not defined on the draft
 */
export function getCreateDisplayMessageChatEvent(
  chatSessionId: string,
  draft: ChatDraftState,
  createParams?: CreateParams
): CreateDisplayMessageChatEvent {
  const {message, settings} = draft;
  if (!settings) {
    throw new Error(`Cannot create display message: settings not defined for chat session ${chatSessionId}`);
  }
  const contextIds = createParams?.contextIds;
  const fileUploads = getCompletedUploads(message.selectedFiles);
  return {
    chatSessionId,
    agentType: settings.agentType,
    messageBlocks: [
      {type: 'text', text: message.text},
      ...fileUploads.map((fileUpload) => ({
        type: 'file' as const,
        source: {type: 's3' as const, s3Location: fileUpload.s3Location},
        filename: fileUpload.filename,
        mediaType: fileUpload.mediaType,
      })),
    ],
    // TODO: userChatContext should eventually come from the draft
    userChatContext: getUserChatContext({
      contextIds: contextIds ?? getContextIdsFromActiveWorkspace(),
    }),
    sources: getSources(settings),
    retry: createParams?.retry,
  };
}

// ============================================
// Global Client State
// ============================================

const ChatDraftGlobalClientState = makeUseGlobalClientState<ChatDraftState | null>(null);

// ============================================
// Key helpers
// ============================================

/**
 * Get the draft key for a chat. Uses NEW_CHAT_DRAFT_KEY for new chats (null chatSessionId).
 */
function getDraftKey(chatSessionId: string | null): string {
  return chatSessionId ?? NEW_CHAT_DRAFT_KEY;
}

// ============================================
// Non-reactive getters/setters (for use outside components)
// ============================================

/**
 * Set or clear the draft state for a chat.
 *
 * @package - ChatActions is the public interface for doing writes.
 */
export function setDraftState(chatSessionId: string | null, state: ChatDraftState | null): void {
  ChatDraftGlobalClientState.updateState(getDraftKey(chatSessionId), state);
}

/**
 * Get the current draft state for a chat.
 */
export function getDraftState(chatSessionId: string | null): ChatDraftState | null {
  return ChatDraftGlobalClientState.getState(getDraftKey(chatSessionId));
}

/**
 * Build the default draft state for a chat.
 * - For new chats: includes the default agent type so the input is immediately usable
 * - For existing chats: settings is undefined until seeded by useMessageThread
 */
function getDefaultDraftState(chatSessionId: string | null): ChatDraftState {
  const isNewChat = chatSessionId == null;
  const defaultAgentType = isNewChat ? getDefaultAgentType() : null;
  return {
    status: ChatDraftStatus.DRAFT,
    message: EMPTY_MESSAGE,
    settings: defaultAgentType ? {agentType: defaultAgentType, publicDataEnabled: false} : undefined,
  };
}

/**
 * Get the draft state, creating one with defaults if it doesn't exist.
 *
 * This handles the case where imperative code (e.g., selectFilesForDraft on file drop)
 * needs the draft before it has been written to global state. The created draft uses
 * getDefaultDraftState, so it matches the fallback that useChatDraftState computes
 * for rendering.
 */
export function getOrCreateDraftState(chatSessionId: string | null): ChatDraftState {
  const existing = getDraftState(chatSessionId);
  if (existing) {
    return existing;
  }

  const newState = getDefaultDraftState(chatSessionId);
  setDraftState(chatSessionId, newState);
  return newState;
}

/**
 * Clear the message content from a draft but preserve settings (agentType, publicDataEnabled).
 * Use this after successful submission to keep the user's settings for their next message.
 */
export function clearDraftMessageContent(chatSessionId: string | null): void {
  const state = getDraftState(chatSessionId);
  if (state) {
    setDraftState(chatSessionId, {
      ...state,
      status: ChatDraftStatus.DRAFT,
      message: {
        text: '',
        selectedFiles: [],
      },
    });
  }
}

/**
 * Seed the draft state with settings from a loaded message.
 *
 * This function fills in settings while preserving user's work:
 * - If no draft exists, creates a new draft with the settings
 * - If a draft exists but settings is undefined, fills it in
 * - User's text and files are always preserved
 */
export function seedDraftStateFromMessage(
  chatSessionId: string | null,
  newSettings: ChatDraftSettings
): void {
  const state = getDraftState(chatSessionId);

  if (!state) {
    // No draft exists - create a new one with the settings
    setDraftState(chatSessionId, {
      status: ChatDraftStatus.DRAFT,
      message: EMPTY_MESSAGE,
      settings: newSettings,
    });
    return;
  }

  if (state.settings != null) {
    // Settings already exist - nothing to fill in
    return;
  }

  setDraftState(chatSessionId, {
    ...state,
    settings: newSettings,
  });
}

// ============================================
// File Management
// ============================================

/**
 * Replace a file in the draft with a new version.
 */
export function replaceFileInDraft(
  chatSessionId: string | null,
  key: string,
  newFile: ChatSelectedFile
): void {
  const state = getDraftState(chatSessionId);
  if (!state) {
    return;
  }

  setDraftState(chatSessionId, {
    ...state,
    message: {
      ...state.message,
      selectedFiles: state.message.selectedFiles.map((f) => (f.key === key ? newFile : f)),
    },
  });
}

/**
 * Remove a file from the draft by key.
 */
export function removeFileFromDraft(chatSessionId: string | null, key: string): void {
  const state = getDraftState(chatSessionId);
  if (!state) {
    return;
  }

  setDraftState(chatSessionId, {
    ...state,
    message: {
      ...state.message,
      selectedFiles: state.message.selectedFiles.filter((f) => f.key !== key),
    },
  });
}

// ============================================
// Utility functions
// ============================================

/**
 * Check if a draft has meaningful message content (not just settings changes).
 * Used to determine if we should show draft indicators.
 */
export function hasMeaningfulDraft(state: ChatDraftState | null): boolean {
  if (!state || state.status !== ChatDraftStatus.DRAFT) {
    return false;
  }
  // Only consider it a meaningful draft if there's text or file content
  return state.message.text.trim().length > 0 || state.message.selectedFiles.length > 0;
}

/**
 * Get a preview of the draft message for display (e.g., in tooltips).
 * Truncates long messages.
 */
export function getDraftPreview(state: ChatDraftState | null, maxLength: number = 50): string | null {
  if (!hasMeaningfulDraft(state)) {
    return null;
  }
  const text = state!.message.text.trim();
  if (text.length === 0) {
    const fileCount = state!.message.selectedFiles.length;
    return `${fileCount} file${fileCount === 1 ? '' : 's'} attached`;
  }
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.substring(0, maxLength)}...`;
}

// ============================================
// React hooks
// ============================================

/**
 * Hook to use the draft state for a specific chat with React subscription.
 * Always returns a draft state object (never null).
 */
export function useChatDraftState(
  chatSessionId: string | null
): [ChatDraftState, (state: ChatDraftState | null) => void] {
  const key = getDraftKey(chatSessionId);
  const [state, setState] = ChatDraftGlobalClientState.useGlobalClientState(key);
  const draftState = useMemo(() => state ?? getDefaultDraftState(chatSessionId), [state, chatSessionId]);
  return [draftState, setState];
}
